import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import WhoWeAre from "./pages/WhoWeAre";
import WhoWeServe from "./pages/WhoWeServe";
import HowToSupport from "./pages/HowToSupport";
import News from "./pages/News";
import Regions from "./pages/Regions";
import Industries from "./pages/Industries";
import Videos from "./pages/Videos";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Admin from "./pages/admin/Admin";
import AdminLatestNews from "./pages/admin/AdminLatestNews";
import AdminNewsletters from "./pages/admin/AdminNewsletters";
import AdminUpcomingEvents from "./pages/admin/AdminUpcomingEvents";
import Profile from "./pages/Profile";
import { Navigate } from "react-router-dom";
import { isLoggedIn, isAdmin } from "./api/controller/userType";
import React, { useEffect, useState } from "react";
import NextSteps from "./pages/NextSteps";

import { FaArrowUp } from 'react-icons/fa'; 


// ScrollToTopButton Component ---
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    // scrolled past 300px
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' 
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    // Cleanup function
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="
            fixed bottom-4 right-4 z-50
            bg-[#668ac9] hover:bg-[#668ac9]
            text-white font-bold
            p-4 rounded-full shadow-lg
            transition-opacity duration-300 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
            transform hover:scale-110
          "
          aria-label="Scroll to top"
        >
        <FaArrowUp className="w-4 h-4" /> 
        </button>
      )}
    </>
  );
};


// ProtectedRoute component
function IsLoginProtectedRoute({ element }) {
  return isLoggedIn() ? element : <Navigate to="/login" replace />;
}

// AccessDeniedWithRedirect component
function AccessDeniedWithRedirect() {
  const [redirect, setRedirect] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setRedirect(true), 5000);
    return () => clearTimeout(timer);
  }, []);
  if (redirect) {
    return <Navigate to="/" replace />;
  }
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center p-6">
      <h1 className="text-4xl font-bold mb-4 text-red-600">Access Denied</h1>
      <p className="text-lg mb-6">You are not an admin and cannot access this page.</p>
      <p className="text-md mb-6">You will be redirected to the home page in 5 seconds.</p>
    </div>
  );
}

// Admin ProtectedRoute component
function IsAdminProtectedRoute({ element }) {
  if (isAdmin()) {
    return element;
  }
  if (isLoggedIn()) {
    return <AccessDeniedWithRedirect />;
  }
  return <Navigate to="/" replace />;
}

// Fallback page for invalid routes
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center p-6">
      <h1 className="text-4xl font-bold mb-4 text-red-600">404</h1>
      <p className="text-lg mb-6">Oops! The page you’re looking for doesn’t exist.</p>
      <a
        href="/"
        className="px-6 py-3 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
      >
        Go Back Home
      </a>
    </div>
  );
}

// Wrapper to hide Navbar/Footer on specific routes
function Layout({ children }) {
  const location = useLocation();
  const hideLayout = location.pathname === "/login"; // hide for login

  return (
    <>
      {!hideLayout && <Navbar />}
      <div className="min-h-screen">{children}</div>
      {!hideLayout && <Footer />}

      {!hideLayout && <ScrollToTopButton />}
    </>
  );
}

function App() {
  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/who-we-are" element={<WhoWeAre />} />
          <Route path="/who-we-serve" element={<WhoWeServe />} />
          <Route path="/how-to-support" element={<HowToSupport />} />
          <Route path="/news" element={<News />} />
          <Route path="/video-platform/regions" element={<Regions />} />
          <Route path= "/video-platform/nextsteps" element={<NextSteps />}/>
          <Route
            path="/video-platform/regions/:region/industries"
            element={<IsLoginProtectedRoute element={<Industries />} />}
          />
          <Route
            path="/video-platform/industries"
            element={<IsLoginProtectedRoute element={<Industries />} />}
          />
          <Route
            path="/video-platform/regions/:region/industries/:industry/videos"
            element={<IsLoginProtectedRoute element={<Videos />} />}
          />
          <Route
            path="/video-platform/videos"
            element={<IsLoginProtectedRoute element={<Videos />} />}
          />
          <Route path="/contact" element={<Contact />} />
          <Route path="/profile" element={<IsLoginProtectedRoute element={<Profile />} />} />
          <Route path="/admin" element={<IsAdminProtectedRoute element={<Admin />} />} />
          <Route path="/admin-latest-news" element={<IsAdminProtectedRoute element={<AdminLatestNews />} />} />
          <Route path="/admin-newsletters" element={<IsAdminProtectedRoute element={<AdminNewsletters />} />} />
          <Route path="/admin-upcoming-events" element={<IsAdminProtectedRoute element={<AdminUpcomingEvents />} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;